// 既読コーパス RAG チャット画面
// ユーザー入力 → 既存埋め込みで類似メモ検索 → Worker に messages + context 送信 → 応答表示
// セッション単位で aiHistory に保存（type='chat'）。過去セッションは入力フォームから開ける。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
} from '../ui.js';
import { fetchChatRag, fetchEmbeddings, fetchAllNotesParallel } from '../api.js';
import {
  getAllBooks,
  upsertBooks,
  getAllEmbeddings,
  saveEmbedding,
  saveAiHistory,
  getAiHistoryByType,
  appendChatMessage,
  updateAiHistory,
} from '../cache.js';

const TOP_K = 5;

// 現在のセッション状態（履歴 ID + messages）
let session = null;

export async function showChatScreen() {
  const histItems = await getAiHistoryByType('chat');
  if (histItems.length > 0) {
    renderEntry(histItems);
  } else {
    startNewSession();
  }
}

function renderEntry(histItems) {
  const html = `
    ${screenHead('読書 AI と対話')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      あなたの読書メモ全体から関連箇所を引きながら、AI と対話できます。<br/>
      「読書中に思いついた問い」「同じテーマで読んだ複数の本を統合してほしい」など、自由に投げてください。
    </p>
    <button class="btn btn--ink btn--block" id="btn-new" type="button">
      <svg class="icon"><use href="#i-chat"/></svg>
      <span>新しい対話を始める</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 + text-embedding-3-small · 1 回 約 3-8 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('chat', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-new').addEventListener('click', () => startNewSession());
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => resumeSession(entry),
    () => showChatScreen()
  );
}

function startNewSession() {
  session = { id: null, messages: [], titleSet: false };
  renderChatView();
}

async function resumeSession(entry) {
  session = {
    id: entry.id,
    messages: entry.payload?.messages || [],
    titleSet: !!entry.title,
  };
  renderChatView();
}

function renderChatView() {
  const html = `
    ${screenHead('読書 AI と対話')}
    <div id="chat-thread" class="chat-thread"></div>
    <div class="chat-input">
      <textarea id="chat-input" class="form-textarea" rows="2" placeholder="例: 写真と引用について、私のメモを踏まえて話したい"></textarea>
      <button class="btn btn--ink btn--small" id="chat-send" type="button" aria-label="送信">
        <svg class="icon"><use href="#i-arrow-right"/></svg>
      </button>
    </div>
  `;
  const screen = showScreen('chat', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  paintThread();
  const input = screen.querySelector('#chat-input');
  const send = screen.querySelector('#chat-send');
  send.addEventListener('click', () => sendMessage(input.value));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendMessage(input.value);
    }
  });
  setTimeout(() => input.focus(), 100);
}

function paintThread() {
  const thread = document.getElementById('chat-thread');
  if (!thread) return;
  thread.innerHTML = (session.messages || [])
    .map((m) => {
      const cls = m.role === 'user' ? 'chat-msg chat-msg--user' : 'chat-msg chat-msg--ai';
      return `
        <div class="${cls}">
          <p class="chat-msg__role">${m.role === 'user' ? 'YOU' : 'LECTIO AI'}</p>
          <p class="chat-msg__body">${escapeHtml(m.content || '').replace(/\n/g, '<br/>')}</p>
          ${
            m.sources && m.sources.length
              ? `<p class="chat-msg__sources">参照: ${m.sources.map((s) => escapeHtml(s)).join(' · ')}</p>`
              : ''
          }
        </div>
      `;
    })
    .join('');
  // スクロールを末尾へ
  setTimeout(() => { thread.scrollTop = thread.scrollHeight; }, 50);
}

async function sendMessage(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return;
  if (!session) startNewSession();

  // ユーザーメッセージを追加・描画
  const userMsg = { role: 'user', content: trimmed };
  session.messages.push(userMsg);
  document.getElementById('chat-input').value = '';
  document.getElementById('chat-input').disabled = true;
  document.getElementById('chat-send').disabled = true;
  paintThread();

  // 履歴エントリが無ければ新規作成
  if (!session.id) {
    const id = await saveAiHistory({
      type: 'chat',
      title: trimmed.slice(0, 30),
      subtitle: '',
      context: {},
      payload: { messages: [userMsg] },
    });
    session.id = id;
    session.titleSet = true;
  } else {
    await appendChatMessage(session.id, userMsg);
  }

  try {
    // 1. 全書籍を取得しメモ補完
    const all = await getAllBooks();
    const eligible = all.filter((b) => (b.note || '').trim().length > 60);
    const missingNotes = eligible.filter((b) => !b.note).length;
    if (missingNotes > 0) {
      await fetchAllNotesParallel(eligible);
      await upsertBooks(eligible);
    }
    // 2. 埋め込み（未生成分のみ）
    await ensureChatEmbeddings(eligible);
    // 3. クエリ埋め込み + 類似ペア検索
    const queryEmb = await fetchEmbeddings([trimmed]);
    const queryVec = queryEmb.vectors[0];
    const allEmb = await getAllEmbeddings();
    const scored = allEmb
      .map((e) => {
        const book = eligible.find((b) => b.id === e.bookId);
        if (!book) return null;
        return { book, score: cosine(queryVec, e.vector) };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, TOP_K);
    // 4. context 構築（書名 + メモ抜粋）
    const context = scored.map((s) => ({
      name: s.book.name,
      author: s.book.author,
      snippet: extractSnippet(s.book.note, trimmed),
      score: s.score,
    }));
    // 5. AI 呼び出し（messages 全体を送る）
    const messages = session.messages.map((m) => ({ role: m.role, content: m.content }));
    const res = await fetchChatRag(messages, context);
    const aiMsg = {
      role: 'assistant',
      content: res.text || '（応答が空でした）',
      sources: context.map((c) => c.name),
    };
    session.messages.push(aiMsg);
    await appendChatMessage(session.id, aiMsg);
    paintThread();
  } catch (e) {
    console.error(e);
    toast('応答の取得に失敗しました');
    // 直前のユーザーメッセージは残す
  } finally {
    const inp = document.getElementById('chat-input');
    const snd = document.getElementById('chat-send');
    if (inp) inp.disabled = false;
    if (snd) snd.disabled = false;
    inp?.focus();
  }
}

function cosine(a, b) {
  let dot = 0, na = 0, nb = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) { dot += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i]; }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom ? dot / denom : 0;
}

function quickHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return String(h);
}

function buildSource(book) {
  const note = (book.note || '').trim();
  if (!note) return '';
  return `${book.name}\n${book.author || ''}\n\n${note}`.slice(0, 7000);
}

async function ensureChatEmbeddings(books) {
  const have = await getAllEmbeddings();
  const haveMap = new Map(have.map((e) => [e.bookId, e]));
  const need = [];
  for (const b of books) {
    const src = buildSource(b);
    if (!src) continue;
    const hash = quickHash(src);
    const cached = haveMap.get(b.id);
    if (!cached || cached.sourceHash !== hash) {
      need.push({ book: b, src, hash });
    }
  }
  if (need.length === 0) return;
  showLoading(`書籍を埋め込み中… (0/${need.length})`);
  try {
    const texts = need.map((x) => x.src);
    const { vectors } = await fetchEmbeddings(texts);
    for (let i = 0; i < need.length; i++) {
      await saveEmbedding(need[i].book.id, vectors[i], need[i].hash);
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `書籍を埋め込み中… (${i + 1}/${need.length})`;
    }
  } finally {
    hideLoading();
  }
}

// メモから、クエリに最も近い段落を切り出す
function extractSnippet(note, query) {
  if (!note) return '';
  const paras = note.split(/\n{2,}/).filter((p) => p.trim().length > 30);
  if (paras.length === 0) return note.slice(0, 500);
  const terms = query.split(/\s+/).filter((t) => t.length >= 2);
  let best = paras[0];
  let bestHits = 0;
  for (const p of paras) {
    const hits = terms.reduce((n, t) => n + (p.includes(t) ? 1 : 0), 0);
    if (hits > bestHits) {
      best = p;
      bestHits = hits;
    }
  }
  return best.length > 500 ? best.slice(0, 500) + '…' : best;
}
